import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { 
  StepHistoryResponse, 
  StepHistoryData, 
  StepHistoryCountResponse,
  StepHistoryEnumsResponse
} from './stephistory.types';

// GET step history enums (status options)
export async function fetchStepHistoryEnums({
  clientCode,
  firstResult = -1,
  maxResult = 0,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
}): Promise<string[]> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOBS.STEP_HISTORY_ENUMS);
  const params: Record<string, any> = {
    firstResult,
    maxResult,
  };
  
  const response: StepHistoryEnumsResponse = await api.get<StepHistoryEnumsResponse>(endpoint, params);
  
  // Response format: [["SUCCESS","FAILURE"]]
  // Extract the first array if it exists
  if (response && response.length > 0 && Array.isArray(response[0])) {
    return response[0];
  }
  return [];
}

// GET step history list
export async function fetchStepHistory({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  stepId,
  clientId,
  jobId,
  request,
  response,
  status,
  lastUpdateDate,
  lastUpdateDateValue,
  searched = true,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  stepId?: number | string;
  clientId?: number;
  jobId?: number;
  request?: string;
  response?: string;
  status?: string | string[];
  lastUpdateDate?: string;
  lastUpdateDateValue?: string;
  searched?: boolean;
}): Promise<StepHistoryData[]> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOBS.STEP_HISTORY);
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  if (stepId !== undefined && stepId !== null && stepId !== '') {
    params.stepId = stepId;
  }
  if (clientId !== undefined && clientId !== null) {
    params.clientId = clientId;
  }
  if (jobId !== undefined && jobId !== null) {
    params.jobId = jobId;
  }
  if (request !== undefined && request !== null && request !== '') {
    params.request = request;
  }
  if (response !== undefined && response !== null && response !== '') {
    params.response = response;
  }
  if (status !== undefined && status !== null && status !== '') {
    // If status is an array, convert it to JSON string format
    if (Array.isArray(status)) {
      params.status = JSON.stringify(status);
    } else {
      params.status = status;
    }
  }
  if (lastUpdateDate !== undefined && lastUpdateDate !== null && lastUpdateDate !== '') {
    params.lastUpdateDate = lastUpdateDate;
  }
  if (lastUpdateDateValue !== undefined && lastUpdateDateValue !== null && lastUpdateDateValue !== '') {
    params.lastUpdateDateValue = lastUpdateDateValue;
  }

  const responseData: StepHistoryResponse = await api.get<StepHistoryResponse>(endpoint, params);
  
  // Convert array of arrays to StepHistoryData objects
  return responseData.map((row) => ({
    stepHistoryId: row[0] || 0,
    stepId: row[1] || 0,
    configObjectJson: row[2] || 0,
    response: row[3] || '',
    date: row[4] || 0,
    status: row[5] || '',
    header: row[6] || '',
    request: row[7] || '',
    jobId: row[8] || 0,
  }));
}

// GET step history count
export async function fetchStepHistoryCount({
  clientCode,
  stepId,
  clientId,
  jobId,
  request,
  response,
  status,
  lastUpdateDate,
  lastUpdateDateValue,
  searched = true,
}: {
  clientCode: string;
  stepId?: number | string;
  clientId?: number;
  jobId?: number;
  request?: string;
  response?: string;
  status?: string;
  lastUpdateDate?: string;
  lastUpdateDateValue?: string;
  searched?: boolean;
}): Promise<number> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOBS.STEP_HISTORY_COUNT);
  const params: Record<string, any> = {
    searched,
  };

  if (stepId !== undefined && stepId !== null && stepId !== '') {
    params.stepId = stepId;
  }
  if (clientId !== undefined && clientId !== null) {
    params.clientId = clientId;
  }
  if (jobId !== undefined && jobId !== null) {
    params.jobId = jobId;
  }
  if (request !== undefined && request !== null && request !== '') {
    params.request = request;
  }
  if (response !== undefined && response !== null && response !== '') {
    params.response = response;
  }
  if (status !== undefined && status !== null && status !== '') {
    params.status = status;
  }
  if (lastUpdateDate !== undefined && lastUpdateDate !== null && lastUpdateDate !== '') {
    params.lastUpdateDate = lastUpdateDate;
  }
  if (lastUpdateDateValue !== undefined && lastUpdateDateValue !== null && lastUpdateDateValue !== '') {
    params.lastUpdateDateValue = lastUpdateDateValue;
  }

  try {
    const response = await api.get<any>(endpoint, params);
    
    // Handle different response formats
    if (typeof response === 'number') {
      // API returns count directly as number
      return response;
    } else if (response && typeof response === 'object' && 'count' in response) {
      // API returns object with count property
      const countValue = (response as any).count;
      return typeof countValue === 'number' ? countValue : 0;
    } else if (typeof response === 'string') {
      // Handle response like "1 8" - extract the count
      const parts = response.trim().split(' ');
      if (parts.length > 0) {
        const countValue = parseInt(parts[parts.length - 1], 10);
        return isNaN(countValue) ? 0 : countValue;
      }
      return 0;
    }
    
    logger.warn('Unexpected count response format:', response);
    return 0;
  } catch (error) {
    logger.error('Error fetching step history count:', error);
    return 0;
  }
}

// GET step history detail by ID (for detailed trace log view)
export async function fetchStepHistoryDetail({
  clientCode,
  stepHistoryId,
}: {
  clientCode: string;
  stepHistoryId: number;
}): Promise<StepHistoryData | null> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.JOBS.STEP_HISTORY}/${stepHistoryId}`);
  
  try {
    const responseData: StepHistoryResponse = await api.get<StepHistoryResponse>(endpoint);
    
    // Response is array of arrays, get first item
    if (responseData && responseData.length > 0 && responseData[0]) {
      const row = responseData[0];
      return {
        stepHistoryId: row[0] || 0,
        stepId: row[1] || 0,
        configObjectJson: row[2] || 0,
        response: row[3] || '',
        date: row[4] || 0,
        status: row[5] || '',
        header: row[6] || '',
        request: row[7] || '',
        jobId: row[8] || 0,
      };
    }
    return null;
  } catch (error) {
    logger.error('Error fetching step history detail:', error);
    throw error;
  }
}